import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { CycleToggle } from "@/components/billing/CycleToggle";
import { toast } from "sonner";
import { useGroupStore } from "@/stores/groupStore";
import { useCountryStore } from "@/stores/countryStore";
import { getApiErrorMessage } from "@/stores/authStore";
import api from "@/lib/api";
import { todayIsoDate } from "@/lib/utils";
import type { PricingTier } from "@/types";

interface CreateGroupDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

interface FormData {
  name: string;
  address: string;
  countryId: string;
  district: string;
  state: string;
  foundedAt: string;
  isRegistered: boolean;
  registrationDate: string;
  description: string;
}

export function CreateGroupDialog({ open, onOpenChange }: CreateGroupDialogProps) {
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [selectedTierUuid, setSelectedTierUuid] = useState<string | null>(null);
  const [cycle, setCycle] = useState<"monthly" | "annual">("monthly");

  // The free tier has no meaningful billing cycle; hiding it behind the
  // toggle would make the free option look withdrawn, so it shows on both.
  const visibleTiers = tiers.filter(
    (tier) => tier.isFree || tier.billingCycle === cycle,
  );

  const { createGroup } = useGroupStore();
  const { countries, isLoading: countriesLoading, fetchCountries } = useCountryStore();

  const { register, handleSubmit, control, watch, formState: { errors }, reset } = useForm<FormData>({
    mode: "onBlur",
    defaultValues: {
      name: "",
      address: "",
      countryId: "",
      district: "",
      state: "",
      foundedAt: "",
      isRegistered: false,
      registrationDate: "",
      description: "",
    },
  });

  const handleCycleChange = (next: "monthly" | "annual") => {
    setCycle(next);
    // PricingTier (and the API resource behind it) only ever exposes `uuid`,
    // never a numeric `id` - tiers must be identified by uuid.
    const selectedStillVisible = tiers.some(
      (t) => t.uuid === selectedTierUuid && (t.isFree || t.billingCycle === next),
    );
    if (!selectedStillVisible) {
      const free = tiers.find((t) => t.isFree);
      const firstOfCycle = tiers.find((t) => t.billingCycle === next);
      setSelectedTierUuid(free?.uuid ?? firstOfCycle?.uuid ?? null);
    }
  };

  const isRegistered = watch("isRegistered");

  // Backend: name/address are required, min:2, max:255; district/state are
  // optional but min:2, max:255 when given.
  const lengthRules = (label: string) => ({
    validate: (v: string) => !v || v.trim().length >= 2 || `${label} must be at least 2 characters.`,
    maxLength: { value: 255, message: `${label} must be at most 255 characters.` },
  });
  const notInFuture = (label: string) => (v: string) =>
    !v || v <= todayIsoDate() || `${label} cannot be in the future.`;

  useEffect(() => {
    if (open) {
      fetchCountries();
      reset();
      setError("");
      setCycle("monthly");
      api.get<{ data: PricingTier[] }>("/pricing-tiers")
        .then((res) => {
          // A group being created has exactly one member, so a tier with a
          // minimum above that can never be applied here. Offering it would
          // create the group and then fail the plan, leaving the user with a
          // warning toast. It stays visible in Billing settings, marked
          // unavailable, where the group can actually grow into it.
          const active = res.data.data.filter(
            (t) => t.isActive && (t.minUsers == null || t.minUsers <= 1),
          );
          setTiers(active);
          const free = active.find((t) => t.isFree);
          setSelectedTierUuid(free?.uuid ?? active[0]?.uuid ?? null);
        })
        .catch(() => setTiers([]));
    }
  }, [open, fetchCountries, reset]);

  const onSubmit = async (data: FormData) => {
    setError("");
    setIsSubmitting(true);
    try {
      const group = await createGroup({
        name: data.name,
        address: data.address,
        country_code: data.countryId,
        district: data.district || undefined,
        state: data.state || undefined,
        founded_at: data.foundedAt || undefined,
        is_registered: data.isRegistered,
        registration_date: data.isRegistered && data.registrationDate ? data.registrationDate : undefined,
        description: data.description || undefined,
      });

      const selectedTier = tiers.find((t) => t.uuid === selectedTierUuid);
      if (selectedTier && !selectedTier.isFree && group?.uuid) {
        try {
          await api.post("/subscriptions", {
            group_uuid: group.uuid,
            pricing_tier_uuid: selectedTier.uuid,
          });
        } catch {
          toast.warning(`Group created, but could not apply the "${selectedTier.name}" plan. You can change it in Billing settings.`);
          onOpenChange(false);
          reset();
          return;
        }
      }

      toast.success(`Group "${data.name}" created successfully!`);
      onOpenChange(false);
      reset();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { reset(); setError(""); } }}>
      <DialogContent className="sm:max-w-[540px] flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Create New Group</DialogTitle>
          <DialogDescription>
            Set up a new savings group and invite members to join.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 space-y-4 pr-1">

            {/* Required fields */}
            <div className="space-y-2">
              <Label htmlFor="cg-name" required>Group Name</Label>
              <Input
                id="cg-name"
                placeholder="e.g., Family Savings Circle"
                aria-invalid={!!errors.name}
                {...register("name", {
                  required: "Group name is required.",
                  ...lengthRules("Group name"),
                })}
              />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cg-address" required>Address</Label>
              <Input
                id="cg-address"
                placeholder="e.g., 123 Main St"
                aria-invalid={!!errors.address}
                {...register("address", {
                  required: "Address is required.",
                  ...lengthRules("Address"),
                })}
              />
              {errors.address && <p className="text-xs text-destructive">{errors.address.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cg-country" required>Country</Label>
              <Controller
                name="countryId"
                control={control}
                rules={{ required: "Country is required." }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      id="cg-country"
                      aria-invalid={!!errors.countryId}
                    >
                      <SelectValue placeholder={countriesLoading ? "Loading..." : "-- Select country --"} />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.countryId && <p className="text-xs text-destructive">{errors.countryId.message}</p>}
            </div>

            {/* Optional location fields */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="cg-district">District</Label>
                <Input
                  id="cg-district"
                  placeholder="e.g., Central"
                  aria-invalid={!!errors.district}
                  {...register("district", lengthRules("District"))}
                />
                {errors.district && <p className="text-xs text-destructive">{errors.district.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="cg-state">State / Province</Label>
                <Input
                  id="cg-state"
                  placeholder="e.g., Eastern Province"
                  aria-invalid={!!errors.state}
                  {...register("state", lengthRules("State / province"))}
                />
                {errors.state && <p className="text-xs text-destructive">{errors.state.message}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cg-founded">Founded At</Label>
              <Input
                id="cg-founded"
                type="date"
                max={todayIsoDate()}
                aria-invalid={!!errors.foundedAt}
                {...register("foundedAt", { validate: notInFuture("Founded date") })}
              />
              {errors.foundedAt && <p className="text-xs text-destructive">{errors.foundedAt.message}</p>}
            </div>

            {/* Registration */}
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Controller
                  name="isRegistered"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="cg-registered"
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(!!checked)}
                    />
                  )}
                />
                <Label htmlFor="cg-registered" className="font-medium cursor-pointer">
                  Officially registered organization
                </Label>
              </div>

              {isRegistered && (
                <div className="space-y-2 pt-1">
                  <Label htmlFor="cg-reg-date">Registration Date</Label>
                  <Input
                    id="cg-reg-date"
                    type="date"
                    max={todayIsoDate()}
                    aria-invalid={!!errors.registrationDate}
                    {...register("registrationDate", { validate: notInFuture("Registration date") })}
                  />
                  {errors.registrationDate && (
                    <p className="text-xs text-destructive">{errors.registrationDate.message}</p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cg-description">Description</Label>
              <Textarea
                id="cg-description"
                placeholder="Describe the purpose of this group..."
                {...register("description")}
                rows={3}
              />
            </div>

            {tiers.length > 0 && (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label>Plan</Label>
                  <CycleToggle cycle={cycle} onChange={handleCycleChange} tiers={tiers} />
                </div>
                <div className="grid gap-2">
                  {visibleTiers.map((tier) => {
                    const isSelected = selectedTierUuid === tier.uuid;
                    const cycleLabel = tier.billingCycle === "annual" ? "yr" : "mo";
                    let price: string;
                    if (tier.isFree) {
                      price = "Free";
                    } else if (tier.pricingModel === "per_user") {
                      price = `${tier.currency} ${tier.formattedPrice} / user / ${cycleLabel}`;
                    } else {
                      price = `${tier.currency} ${tier.formattedPrice} / ${cycleLabel}`;
                    }
                    const memberLimit = tier.maxUsers ? `Up to ${tier.maxUsers} members` : "Unlimited members";
                    return (
                      <button
                        key={tier.uuid}
                        type="button"
                        onClick={() => setSelectedTierUuid(tier.uuid)}
                        className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${
                          isSelected
                            ? "border-primary bg-primary/5 ring-1 ring-primary"
                            : "border-border hover:border-muted-foreground/50"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium text-sm">{tier.name}</span>
                            {tier.isFree && (
                              <Badge variant="secondary" className="text-xs shrink-0">Free</Badge>
                            )}
                            {tier.trialDays > 0 && !tier.isFree && (
                              <Badge variant="outline" className="text-xs shrink-0">{tier.trialDays}-day trial</Badge>
                            )}
                          </div>
                          <span className="text-sm font-semibold shrink-0">{price}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{memberLimit}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="pt-4 mt-4 border-t shrink-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="accent" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Group"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
